import { Group, useAllWorkspaceGroups } from '@gripp/shared-logic';
import { LoadingIndicator, useSlideLayout } from '@gripp/shared-ui';
import { MultiItemSelectorModal } from 'libs/shared-ui/src/components/forms/multiItemSelectorModal';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

type Props = {
  selectedGroups: Group[];
  onGroupsChange: (groups: Group[]) => void;
  onCreateGroup: (name: string) => Promise<Group>;
  workspaceId: string;
};

export const AssetsGroupsPicker = ({
  selectedGroups,
  onGroupsChange,
  onCreateGroup,
  workspaceId,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { setColumnTitle } = useSlideLayout();
  const { groups: availableGroups, isLoading } =
    useAllWorkspaceGroups(workspaceId);

  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const allGroups = useMemo(() => {
    const map = new Map<string, Group>();
    [...availableGroups, ...selectedGroups].forEach((group) => {
      if (group?.id) {
        map.set(group.id, { id: group.id, name: group.name });
      }
    });
    return Array.from(map.values());
  }, [availableGroups, selectedGroups]);

  const selectedGroupIds = useMemo(
    () => new Set(selectedGroups.map((g) => g.id)),
    [selectedGroups]
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const base = query
      ? allGroups.filter((g) => g.name.toLowerCase().includes(query))
      : allGroups;

    return base.map((g) => ({
      id: g.id,
      name: g.name,
      isActive: selectedGroupIds.has(g.id),
    }));
  }, [allGroups, search, selectedGroupIds]);

  const canCreateItem =
    !!search.trim() &&
    !allGroups.some(
      (g) => g.name.toLowerCase() === search.trim().toLowerCase()
    );

  const handleItemToggle = (item: Group) => {
    if (selectedGroupIds.has(item.id)) {
      onGroupsChange(selectedGroups.filter((g) => g.id !== item.id));
      return;
    }
    onGroupsChange([...selectedGroups, { id: item.id, name: item.name }]);
  };

  const handleToggleAll = (selectAll: boolean) => {
    if (selectAll) {
      const toAdd = filteredItems
        .filter((i) => !selectedGroupIds.has(i.id))
        .map((i) => ({ id: i.id, name: i.name }));
      onGroupsChange([...selectedGroups, ...toAdd]);
    } else {
      const filteredIds = new Set(filteredItems.map((i) => i.id));
      onGroupsChange(selectedGroups.filter((g) => !filteredIds.has(g.id)));
    }
  };

  const handleCreatePress = async () => {
    if (!search.trim()) return;
    setIsCreating(true);
    try {
      const created = await onCreateGroup(search.trim());
      onGroupsChange([...selectedGroups, created]);
      setSearch('');
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    const title = `${t('asset.groups')} (${selectedGroups.length})`;
    i18n.addResource(i18n.language, 'translation', title, title);
    setColumnTitle(title);
  }, [setColumnTitle, t, i18n, selectedGroups.length]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <View style={styles.container}>
      <MultiItemSelectorModal
        showOptions
        search={search}
        setSearch={setSearch}
        searchPlaceholder={t('groups.common.selectGroup')}
        filteredItems={filteredItems}
        onItemToggle={handleItemToggle}
        onToggleAll={handleToggleAll}
        canCreateItem={canCreateItem}
        onCreateItem={onCreateGroup}
        onCreatePress={handleCreatePress}
        isCreating={isCreating}
        createButtonText={t('common.create')}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
